/**
 * Compresión de imágenes antes de subirlas.
 *
 * El API responde 413 con archivos que NestJS aceptaría de sobra (su límite es
 * de 10 MB): el corte lo pone la infraestructura que está delante, así que una
 * foto de cámara de 2 MB nunca llega. La app móvil ya resolvía esto reduciendo
 * la foto antes de subirla; esto hace lo mismo en la web, con los mismos
 * valores para que ambas se comporten igual.
 *
 * Aparte del 413, guardar evidencias de varios MB no aporta nada: a 1280px se
 * ven igual en pantalla, suben mucho más rápido y ocupan una fracción en el
 * storage.
 */

/** Ancho máximo. Por encima de esto la foto se reescala manteniendo proporción. */
const ANCHO_MAX = 1280;
/** Calidad del JPEG resultante. 0.5 da ~200-400 KB sin diferencia visible. */
const CALIDAD = 0.5;
/** Por debajo de esto no vale la pena recomprimir: ya cabe de sobra. */
const UMBRAL_BYTES = 300 * 1024;

const esImagenSoportada = (file: File): boolean =>
  /^image\/(jpeg|jpg|png|webp)$/i.test(file.type);

/**
 * Decodifica respetando la orientación EXIF. Las fotos de celular vienen
 * rotadas por metadatos: dibujarlas al canvas sin esto las guarda de lado.
 */
async function decodificar(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file, { imageOrientation: 'from-image' });
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('No se pudo leer la imagen'));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Devuelve una versión reducida del archivo, o el original si no hay nada que
 * ganar o si algo falla. Nunca lanza: si no se puede comprimir, que la subida
 * lo intente con el archivo tal cual en vez de bloquear al usuario.
 */
export async function comprimirImagen(file: File): Promise<File> {
  if (!esImagenSoportada(file)) return file;

  try {
    const origen = await decodificar(file);
    const anchoOriginal = origen.width;
    const altoOriginal = origen.height;
    if (!anchoOriginal || !altoOriginal) return file;

    // Ya es chica en peso y en dimensiones: recomprimir solo la degradaría.
    if (file.size <= UMBRAL_BYTES && anchoOriginal <= ANCHO_MAX) return file;

    const escala = Math.min(1, ANCHO_MAX / anchoOriginal);
    const ancho = Math.round(anchoOriginal * escala);
    const alto = Math.round(altoOriginal * escala);

    const canvas = document.createElement('canvas');
    canvas.width = ancho;
    canvas.height = alto;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;

    // Fondo blanco: un PNG con transparencia se vuelve negro al pasar a JPEG.
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, ancho, alto);
    ctx.drawImage(origen as CanvasImageSource, 0, 0, ancho, alto);
    if ('close' in origen) origen.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', CALIDAD),
    );
    if (!blob || blob.size >= file.size) return file;

    const nombre = file.name.replace(/\.[^.]+$/, '') || 'foto';
    return new File([blob], `${nombre}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch {
    return file;
  }
}
